import type { SimulationNodeDatum, SimulationLinkDatum } from 'd3-force'

export interface TopoNode {
  id: string
  kind: 'gateway' | 'anchor' | 'tag'
  label: string
  active?: boolean
  type?: string | null
  assignedLocationId?: number | null
  rssi?: number | null
  nextHopRssiDbm?: number | null
  nextHopAddress?: string | null
  addr?: string | null
  status?: 'ok' | 'low_batt' | 'in_transit' | 'departed'
  battery_pct?: number | null
  hops?: number | null
  travel_ms?: number | null
}
export interface TopoLink { source: string; target: string; rssi?: number | null }
export type SimNode = TopoNode & SimulationNodeDatum
export type SimLink = SimulationLinkDatum<SimNode> & { rssi?: number | null }
export type Zoom = { k: number; x: number; y: number }
export type Dims = { w: number; h: number }
export type TopoData = { gw_id: string; name: string; nodes: TopoNode[]; links: TopoLink[] }

export const GW_R = 28
export const ANCHOR_R = 14
export const TAG_R = 15
export const radiusOf = (n: TopoNode) => (n.kind === 'gateway' ? GW_R : n.kind === 'anchor' ? ANCHOR_R : TAG_R)

// Technical dark canvas -- deliberately not the app's light theme, matches the Limonfy reference.
export const CANVAS_BG = '#0d1117'
export const C_GRID = '#1b222c'
export const C_GW = '#1f6feb'
export const C_ANCHOR = '#30363d'
export const C_ANCHOR_BORDER = '#768390'
export const C_TEXT = '#c9d1d9'
export const C_DIM = '#6e7681'
export const C_EDGE = '#3d444d'
export const C_OFF = '#f85149'
// Tag color = asset status (wl-status-badge palette), not the reference's per-tag rainbow.
export const STATUS_COLOR: Record<string, string> = { ok: '#3f8f52', low_batt: '#a07c20', in_transit: '#4870b8', departed: '#e8833a' }
export const nodeFill = (n: TopoNode) => (n.kind === 'gateway' ? C_GW : n.kind === 'anchor' ? C_ANCHOR : STATUS_COLOR[n.status ?? 'ok'] ?? '#8a7bff')

export const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v))

// Signal strength → edge length: strong RSSI = short, weak = long.
const RSSI_NEAR = -45
const RSSI_FAR = -95
const DIST_NEAR = 40
const DIST_FAR = 360
export const rssiToDist = (rssi?: number | null) => {
  if (rssi == null) return 220
  const v = clamp(rssi, RSSI_FAR, RSSI_NEAR)
  return DIST_NEAR + ((RSSI_NEAR - v) / (RSSI_NEAR - RSSI_FAR)) * (DIST_FAR - DIST_NEAR)
}
export const rssiToStrength = (rssi?: number | null) => {
  if (rssi == null) return 0.25
  const v = clamp(rssi, RSSI_FAR, RSSI_NEAR)
  return 0.08 + ((v - RSSI_FAR) / (RSSI_NEAR - RSSI_FAR)) * (1 - 0.08)
}

export const POLL_MS = 30000

export const linkId = (e: string | number | SimNode): string => (typeof e === 'object' ? (e as SimNode).id : String(e))

// mode is folded in so switching Organiczny/Hierarchiczny always forces a rebuild.
export const buildSignature = (d: TopoData | null, mode: string) => d
  ? d.nodes.map((n) => `${n.id}${n.kind === 'anchor' && n.active === false ? '!' : ''}`).join(',') + '|' + d.links.map((l) => `${l.source}>${l.target}`).join(',') + '|' + mode
  : ''

// Live infra on an outer ring; offline/unreachable nodes are parked by the caller instead.
export function layoutInfra(nodes: SimNode[], dims: Dims, bottomReserve = 0, links: SimLink[] = []) {
  const { w, h } = dims
  const hh = h - bottomReserve
  const infra = nodes.filter((n) => n.kind === 'gateway' || (n.kind === 'anchor' && n.active !== false))
  const cx = w / 2, cy = hh / 2
  const rx = w * 0.44, ry = hh * 0.41
  infra.forEach((n, i) => {
    const ang = -Math.PI / 2 + (2 * Math.PI * i) / Math.max(1, infra.length)
    n.fx = cx + rx * Math.cos(ang)
    n.fy = cy + ry * Math.sin(ang)
    if (n.x == null) { n.x = n.fx; n.y = n.fy }
  })
  // Seed a new tag near its own linked node, not dead-center, to avoid a "burst from the middle" on load.
  const byId = new Map(nodes.map((n) => [n.id, n]))
  for (const n of nodes) {
    if (n.kind !== 'tag' || n.x != null) continue
    const own = links.find((l) => linkId(l.source) === n.id)
    const parent = own ? byId.get(linkId(own.target)) : undefined
    const baseX = parent?.x ?? parent?.fx ?? cx
    const baseY = parent?.y ?? parent?.fy ?? cy
    n.x = (baseX ?? cx) + (Math.random() - 0.5) * 60
    n.y = (baseY ?? cy) + (Math.random() - 0.5) * 60
  }
}

// Top-down tree: gateway → anchors → tags (grouped under their strongest anchor), positions pinned.
export function layoutHierarchical(nodes: SimNode[], links: SimLink[], dims: Dims, bottomReserve: number) {
  const { w, h } = dims
  const hh = h - bottomReserve
  const gw = nodes.find((n) => n.kind === 'gateway')
  const anchorsLive = nodes.filter((n) => n.kind === 'anchor' && n.active !== false)
  const anchorIds = new Set(anchorsLive.map((a) => a.id))
  const linkedTagIds = new Set<string>()
  for (const l of links) { const s = linkId(l.source); if (s.startsWith('tag:')) linkedTagIds.add(s) }
  const tags = nodes.filter((n) => n.kind === 'tag' && linkedTagIds.has(n.id))

  const bestParent = new Map<string, { parent: string; isAnchor: boolean; rssi: number }>()
  for (const l of links) {
    const s = linkId(l.source), t = linkId(l.target)
    if (!s.startsWith('tag:')) continue
    const isAnchor = anchorIds.has(t)
    if (!isAnchor && t !== gw?.id) continue
    const rssi = typeof l.rssi === 'number' ? l.rssi : -999
    const cur = bestParent.get(s)
    if (!cur || (isAnchor && !cur.isAnchor) || (isAnchor === cur.isAnchor && rssi > cur.rssi)) {
      bestParent.set(s, { parent: t, isAnchor, rssi })
    }
  }
  const tagsOfParent = new Map<string, SimNode[]>()
  for (const tg of tags) {
    const p = bestParent.get(tg.id)?.parent ?? gw?.id
    if (!p) continue
    if (!tagsOfParent.has(p)) tagsOfParent.set(p, [])
    tagsOfParent.get(p)!.push(tg)
  }

  const set = (n: SimNode, x: number, y: number) => { n.fx = x; n.fy = y; n.x = x; n.y = y }
  const cx = w / 2
  const yGw = Math.max(56, hh * 0.14)
  const yAnchor = hh * 0.46
  const yTag = hh * 0.80
  const leftPad = 56, rightPad = 56
  const usableW = Math.max(240, w - leftPad - rightPad)
  if (gw) set(gw, cx, yGw)

  const unitsOf = (id: string) => Math.max(1, tagsOfParent.get(id)?.length ?? 0)
  const gwTags = gw ? (tagsOfParent.get(gw.id) ?? []) : []
  const totalUnits = anchorsLive.reduce((s, a) => s + unitsOf(a.id), 0) + gwTags.length || 1
  const slot = usableW / totalUnits
  let cursor = leftPad
  anchorsLive.forEach((a) => {
    const kids = tagsOfParent.get(a.id) ?? []
    const bandW = unitsOf(a.id) * slot
    set(a, cursor + bandW / 2, yAnchor)
    kids.forEach((k, j) => set(k, cursor + slot * (j + 0.5), yTag + (j % 2) * 26))
    cursor += bandW
  })
  gwTags.forEach((k, j) => set(k, cursor + slot * (j + 0.5), yTag + (j % 2) * 26))
}

// Zoom/pan that fits all current node positions (+ park-row labels) into the panel.
export function computeFitZoom(
  nodes: SimNode[],
  parkLabels: { tags: { x: number; y: number } | null; anchors: { x: number; y: number } | null },
  dims: Dims,
): Zoom | null {
  const ns = nodes.filter((n) => n.x != null)
  if (ns.length < 2) return null
  const xs = ns.map((n) => n.x as number)
  const ys = ns.map((n) => n.y as number)
  for (const lbl of [parkLabels.tags, parkLabels.anchors]) {
    if (lbl) { xs.push(lbl.x, lbl.x + 230); ys.push(lbl.y) }
  }
  const minX = Math.min(...xs) - 46, maxX = Math.max(...xs) + 46
  const minY = Math.min(...ys) - 46, maxY = Math.max(...ys) + 46
  const { w, h } = dims
  const k = clamp(Math.min(w / (maxX - minX), h / (maxY - minY)), 0.4, 1.0)
  return { k, x: (w - k * (minX + maxX)) / 2, y: (h - k * (minY + maxY)) / 2 }
}
